import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Apiservice } from '../../../../services/ApiService';

const DEFAULT_STATUS = 'event_listing_loaded';

@Component({
  selector: 'app-add-performance',
  templateUrl: './add-performance.component.html',
  standalone: false,
  styleUrls: ['./add-performance.component.scss']
})
export class AddPerformanceComponent {
  @Input() venueID: string = '';
  @Input() venueTimezone: string = '';
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  description = '';
  date = '';
  time = '';

  saving = false;
  validationError = '';
  saveError = '';

  constructor(private apiservice: Apiservice) {}

  cancel() {
    this.cancelled.emit();
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget && !this.saving) {
      this.cancel();
    }
  }

  save() {
    this.validationError = this.validate();
    if (this.validationError) { return; }

    const timestamp = this.venueLocalToEpoch(this.date, this.time);
    this.saving = true;
    this.saveError = '';

    this.apiservice.postPerformance({
      venueID: this.venueID,
      timestamp,
      description: this.description.trim(),
      status: DEFAULT_STATUS
    }).subscribe({
      next: () => {
        this.saving = false;
        this.saved.emit();
      },
      error: (err) => {
        this.saving = false;
        this.saveError = typeof err?.error === 'string'
          ? err.error
          : (err?.error?.message || err?.message || 'Failed to save performance. Please try again.');
      }
    });
  }

  private validate(): string {
    if (!this.venueID) { return 'A venue must be selected.'; }
    if (!this.date) { return 'Date is required.'; }
    if (!this.time) { return 'Start time is required.'; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(this.date)) { return 'Date must be YYYY-MM-DD.'; }
    if (!/^\d{2}:\d{2}$/.test(this.time)) { return 'Time must be HH:MM.'; }
    return '';
  }

  private venueLocalToEpoch(dateStr: string, timeStr: string): number {
    // Convert a wall-clock date+time in the venue's timezone to a UTC epoch.
    // We treat the input as UTC ("asIfUTC"), ask Intl what that instant *looks* like
    // in the venue's tz, then subtract the offset between the two — which gives us
    // the real UTC instant whose venue-local rendering matches the user's input.
    // This avoids pulling in a tz library while still handling DST correctly.
    const [y, m, d] = dateStr.split('-').map(Number);
    const [h, min] = timeStr.split(':').map(Number);
    const asIfUTC = Date.UTC(y, m - 1, d, h, min);
    const tz = this.venueTimezone || 'UTC';
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).formatToParts(new Date(asIfUTC));
    const lookup: any = {};
    parts.forEach((p) => (lookup[p.type] = p.value));
    const hour = lookup.hour === '24' ? 0 : Number(lookup.hour);
    const asShown = Date.UTC(
      Number(lookup.year),
      Number(lookup.month) - 1,
      Number(lookup.day),
      hour,
      Number(lookup.minute),
      Number(lookup.second)
    );
    const offset = asShown - asIfUTC;
    return Math.floor((asIfUTC - offset) / 1000);
  }
}
